import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mapToFilter } from "@/components/Filter/FilterUtils";
import type { Filter, FilterElement } from "@/types/SuperFilter";
import { savePersistedFilters } from "@/utils/filterPersistence";

/** Debounce delay for URL sync (ms) */
const URL_SYNC_DEBOUNCE_MS = 400;

/**
 * Merge the selected `filters` into the existing search slice.
 *
 * This is a *merge*, not a rebuild: it starts from `previousState` so any params the
 * filter set doesn't own (free-text search, sort order, color filters, …) survive a
 * filter change. `managedKeys` lists the keys this filter set owns; they are cleared
 * first and the currently-selected ones are written back, so deselecting a filter
 * removes its key while leaving everything else intact.
 *
 * Boolean filters (e.g. a single `true` toggle like onlyDeviations) are stored as a
 * bare boolean rather than an array; this is derived from the selected value keys, so
 * no filter key is special-cased here.
 */
export const buildFilterSearchState = (
  previousState: Record<string, unknown> | undefined,
  filters: Filter[],
  managedKeys: string[] = [],
) => {
  const nextState: Record<string, unknown> = { ...previousState };

  // Clear every key this filter set owns; selected ones are re-added below.
  for (const key of managedKeys) {
    delete nextState[key];
  }

  for (const filter of filters) {
    const values = filter.values.map((option) => option.key);
    if (values.length === 0) {
      continue;
    }

    const key = String(filter.component.key);

    // Boolean toggles are serialised as a bare boolean, not an array.
    if (values.some((value) => typeof value === "boolean")) {
      nextState[key] = values.some(Boolean);
    } else {
      nextState[key] = values;
    }
  }

  return nextState;
};

type UseFiltersOptions = {
  /** Flat filter params extracted from the current route's search (e.g. search.state or search.map) */
  filterSearch: Record<string, unknown> | undefined;
  /** Persists the new filter state to the URL. Hook handles debouncing. */
  navigate: (filterState: Record<string, unknown>) => Promise<void>;
  /** Multiple values per Filter for each FilterElement, instead of one per value. */
  multiple?: boolean;
  persistKey?: string;
};

/**
 * Route-agnostic hook to manage filters based on URL search parameters.
 *
 * The caller supplies `filterSearch` (the relevant slice of the route's search params,
 * e.g. `search.state` or `search.map`) and a `navigate` callback that writes the new
 * flat filter state back to the URL. This keeps routing concerns out of the hook; any
 * route-specific reset (e.g. resetting `page` to 1 on filter change) belongs in the
 * caller's `navigate`.
 *
 * @param filterElements - The filter elements to use for filtering.
 * @param options.filterSearch - Flat object of current filter values from the URL.
 * @param options.navigate - Async function that writes a new filter state to the URL.
 * @param options.multiple - If true, merges values into a single filter per filter component.
 * @param options.persistKey - If set, persists filters to local storage under this key.
 * @returns An object containing:
 *   - selectedFilters: Derived from URL search parameters, either as separate filters for each value or merged based on the flag.
 *   - setSelectedFilters: A function to update the filters (debounced URL sync).
 */
export function useFilters(filterElements: FilterElement[], options: UseFiltersOptions) {
  const { filterSearch, navigate, multiple = false, persistKey } = options;

  // Keys this hook owns in the search slice. Used to clear deselected filters
  // without disturbing params the hook doesn't manage.
  const managedKeys = useMemo(
    () => filterElements.map((fe) => String(fe.item.key)),
    [filterElements],
  );

  // Derive filters from URL params (source of truth on initial load)
  const urlFilters = useMemo(() => {
    const filters: Filter[] = [];
    if (!filterSearch) return filters;

    for (const fe of filterElements) {
      const raw = filterSearch[String(fe.item.key)];

      // Normalise to a string array. Boolean search params (e.g. onlyDeviations)
      // are stored as `true`/`false` rather than an array, so we handle them here.
      let keys: string[] | undefined;
      if (Array.isArray(raw)) {
        keys = raw.map(String);
      } else if (typeof raw === "boolean" && raw) {
        keys = ["true"];
      }

      if (keys && keys.length > 0) {
        if (multiple) {
          filters.push(mapToFilter(fe, keys));
        } else {
          keys.forEach((key) => {
            filters.push(mapToFilter(fe, key));
          });
        }
      }
    }
    return filters;
  }, [filterSearch, filterElements, multiple]);

  // Local state for immediate UI feedback (avoids lag from URL updates)
  const [localFilters, setLocalFilters] = useState<Filter[] | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use local filters if set, otherwise fall back to URL filters
  const selectedFilters = localFilters ?? urlFilters;

  // Sync filters to URL
  const syncToUrl = useCallback(
    async (filters: Filter[]) => {
      await navigate(buildFilterSearchState(filterSearch, filters, managedKeys));
      setLocalFilters(null);
    },
    [navigate, filterSearch, managedKeys],
  );

  // Update local state immediately, debounce URL sync
  const setSelectedFilters = useCallback(
    (updater: Filter[] | ((prev: Filter[]) => Filter[])) => {
      const newFilters = typeof updater === "function" ? updater(selectedFilters) : updater;

      // Update local state immediately for responsive UI
      setLocalFilters(newFilters);

      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Debounce URL sync
      debounceTimerRef.current = setTimeout(() => {
        void syncToUrl(newFilters);
      }, URL_SYNC_DEBOUNCE_MS);
    },
    [selectedFilters, syncToUrl],
  );

  // Save URL-derived filters whenever they change (e.g. filterElements finish loading
  // while the URL already has filter params from a direct link or matrix navigation).
  // SuperFilter's event handlers cover the interactive case; this covers the URL case.
  useEffect(() => {
    if (persistKey && urlFilters.length > 0) {
      savePersistedFilters(persistKey, urlFilters);
    }
  }, [urlFilters, persistKey]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { selectedFilters, setSelectedFilters };
}
